// F1 (SD36, FR-M6a/M7): sourced meeting notes, as a self-contained panel inside the watch
// page. PR3 (#59) owns the real notes-route scaffold, chapters tab and ChapterBar — this
// panel is deliberately independent of that so it is easy to lift into PR3's scaffold once
// that lands (noted in the done file), but it already follows the same API shape PR3 will.
//
// Controls are @grackle/ui (Timestamp chips, Badge, Callout, Button) — SD35, reset PR7.
import { useCallback, useEffect, useRef, useState } from "react";
import { Badge, Icon, Timestamp } from "@grackle/ui";
import { api, ApiFail } from "../api/client";
import type { LlmProviderKind, NoteItemOut, NotesCancelOut, NotesGenerateOut, NotesOut } from "../api/types";
import { formatDuration } from "../library/format";
import { Button, Notice } from "../components/ui";
import { LLM_KIND_LABEL } from "../components/LlmProviderPicker";

const POLL_MS = 8000;

export function itemStartSeconds(item: NoteItemOut): number | null {
  if (item.start_ms != null) return item.start_ms / 1000;
  const first = item.sources.find((s) => s.start_ms != null);
  return first ? (first.start_ms as number) / 1000 : null;
}

/** Every distinct current timestamp an item's sources carry — a summary or chapter line can
 * cite several segments, each with its own chip. Falls back to the item's own stored start
 * when every source has gone stale, so a fully-stale item still carries one chip. */
export function chipSeconds(item: NoteItemOut): number[] {
  const starts = item.sources.map((s) => s.start_ms).filter((v): v is number => v != null);
  const unique = Array.from(new Set(starts)).sort((a, b) => a - b);
  if (unique.length > 0) return unique.map((ms) => ms / 1000);
  const fallback = itemStartSeconds(item);
  return fallback != null ? [fallback] : [];
}

function TimestampChip({ seconds, onSeek }: { seconds: number; onSeek: (seconds: number) => void }) {
  return <Timestamp time={formatDuration(seconds * 1000)} onClick={() => onSeek(seconds)} />;
}

function TimestampChips({ item, onSeek }: { item: NoteItemOut; onSeek: (seconds: number) => void }) {
  const seconds = chipSeconds(item);
  if (seconds.length === 0) return null;
  return (
    <>
      {seconds.map((s) => (
        <TimestampChip key={s} seconds={s} onSeek={onSeek} />
      ))}
    </>
  );
}

function StaleMark() {
  return (
    <Badge size="sm" tone="outline" title="The transcript has changed since this was generated">
      transcript changed
    </Badge>
  );
}

export function provenanceLine(notes: NotesOut): string | null {
  if (!notes.model) return null;
  const kindName = notes.provider_kind
    ? LLM_KIND_LABEL[notes.provider_kind as LlmProviderKind] ?? notes.provider_kind
    : "a connected model";
  const via = notes.scope === "personal" ? `your ${kindName} account` : `your org's ${kindName} account`;
  const dropped =
    notes.dropped_count > 0
      ? ` · ${notes.dropped_count} unsourced line${notes.dropped_count === 1 ? "" : "s"} removed`
      : "";
  return `Generated by ${notes.model} via ${via}${dropped}`;
}

export function notesToMarkdown(notes: NotesOut, grackleId: string): string {
  const link = (item: NoteItemOut) => {
    const s = itemStartSeconds(item);
    return s != null ? ` [${formatDuration(s * 1000)}](/g/${grackleId}?t=${Math.round(s)})` : "";
  };
  const lines: string[] = [];
  if (notes.summary.length) {
    lines.push("## Summary");
    for (const s of notes.summary) lines.push(`- ${s.text}${link(s)}`);
    lines.push("");
  }
  if (notes.decisions.length) {
    lines.push("## Decisions");
    for (const d of notes.decisions) lines.push(`- ${d.text}${link(d)}`);
    lines.push("");
  }
  if (notes.action_items.length) {
    lines.push("## Action items");
    for (const a of notes.action_items) lines.push(`- ${a.owner ? `**${a.owner}:** ` : ""}${a.text}${link(a)}`);
    lines.push("");
  }
  if (notes.chapters.length) {
    lines.push("## Chapters");
    for (const c of notes.chapters) lines.push(`- ${c.text}${link(c)}`);
  }
  return lines.join("\n").trim();
}

function SummaryLine({ item, onSeek }: { item: NoteItemOut; onSeek: (seconds: number) => void }) {
  return (
    <p className="notes-line">
      <span>{item.text}</span> <TimestampChips item={item} onSeek={onSeek} />
      {item.stale ? <StaleMark /> : null}
    </p>
  );
}

function DecisionLine({ item, onSeek }: { item: NoteItemOut; onSeek: (seconds: number) => void }) {
  return (
    <div className="notes-line notes-decision">
      <Icon name="seal-check" size={18} fill className="notes-decision-icon" aria-hidden />
      <span>{item.text}</span> <TimestampChips item={item} onSeek={onSeek} />
      {item.stale ? <StaleMark /> : null}
    </div>
  );
}

function ActionItemLine({ item, onSeek }: { item: NoteItemOut; onSeek: (seconds: number) => void }) {
  return (
    <div className="notes-line notes-action">
      <span>{item.text}</span>
      {item.owner ? <Badge size="sm" tone="outline">{item.owner}</Badge> : null}
      <TimestampChips item={item} onSeek={onSeek} />
      {item.stale ? <StaleMark /> : null}
    </div>
  );
}

/** The panel's visibility rules (decisions 2-3), as a pure function of the API response and
 * the caller's access — kept separate from the stateful container below so the rules
 * themselves (empty state, regen-failure callout gating, when to hide entirely) are testable
 * without rendering anything. */
export interface NotesViewState {
  /** False means render nothing at all — there is nothing a viewer without edit access
   * should see (no notes ever generated, or a first generation that failed). */
  visible: boolean;
  showGenerateButton: boolean;
  showUnavailable: boolean;
  /** An editor canceled before any notes existed: say so, and offer Generate again. */
  showCanceled: boolean;
  showPending: boolean;
  /** Editors can stop a queued, running or failed generation (it then stays stopped). */
  showCancelButton: boolean;
  showCallout: boolean;
  calloutText: string;
  /** The server's `error_reason` line inside the callout, only when it actually sent one. For
   * a failed regenerate it is the provider error — editor-and-up only (decision 3). For an
   * unavailable regenerate that kept the previous notes it is the unavailable reason — shown
   * to everyone (Grackle 2026-10-09). */
  showCalloutError: boolean;
  showItems: boolean;
  showRegenerateButton: boolean;
}

const HIDDEN_VIEW: NotesViewState = {
  visible: false,
  showGenerateButton: false,
  showUnavailable: false,
  showCanceled: false,
  showPending: false,
  showCancelButton: false,
  showCallout: false,
  calloutText: "",
  showCalloutError: false,
  showItems: false,
  showRegenerateButton: false,
};

/** What an `unavailable` panel says, from the server's cause rather than its reason text, so a
 * transcript problem is never blamed on the model. Null when the cause isn't one the panel
 * knows: the panel then falls back to a generic line plus the server's reason. */
export function unavailableMessage(notes: NotesOut): string | null {
  switch (notes.unavailable_cause) {
    case "no_model":
      return "Meeting notes are off until a language model is connected.";
    case "no_transcript":
      return "Notes will be generated once the transcript is ready.";
    case "transcript_unusable":
      return "This recording's transcript can't be used for notes yet.";
    default:
      return null;
  }
}

export function computeNotesView(notes: NotesOut, canEdit: boolean): NotesViewState {
  if (notes.status === "none" && !canEdit) return HIDDEN_VIEW;
  if (notes.status === "failed" && !notes.has_items && !canEdit) return HIDDEN_VIEW;
  if (notes.status === "canceled" && !notes.has_items && !canEdit) return HIDDEN_VIEW;

  const showItems = notes.has_items || notes.status === "ready";
  const pending = notes.status === "queued" || notes.status === "running";
  // Grackle 2026-10-09: an unavailable *regenerate* (e.g. the provider was removed) keeps the
  // previous notes like a failed one, but its reason names no vendor and is shown to everyone
  // as a callout above the kept notes. With nothing kept it stays the plain empty state.
  const unavailableKept = notes.status === "unavailable" && notes.has_items;
  const failedCallout = notes.status === "failed" && canEdit;
  const calloutText = notes.status === "failed"
    ? `Couldn't ${notes.has_items ? "regenerate" : "generate"} notes.${notes.has_items ? " Showing the previous version." : ""}`
    : unavailableKept
      ? "Notes couldn't be regenerated. Showing the previous version."
      : "";

  return {
    visible: true,
    showGenerateButton: notes.status === "none",
    showUnavailable: notes.status === "unavailable" && !notes.has_items,
    showCanceled: notes.status === "canceled" && !notes.has_items,
    showPending: pending,
    showCancelButton: canEdit && (pending || notes.status === "failed"),
    // Decision 3: for a failed regenerate, viewers see the kept notes with no callout at all;
    // editors get the callout, Retry and the one-line provider error. An unavailable
    // regenerate's callout and reason are shown to everyone (Retry stays editor-only).
    showCallout: failedCallout || unavailableKept,
    calloutText,
    showCalloutError: (failedCallout || unavailableKept) && Boolean(notes.error_reason),
    showItems,
    // Canceling a regenerate leaves the previous notes in place, regenerable like ready ones.
    showRegenerateButton: canEdit && (notes.status === "ready" || (notes.status === "canceled" && notes.has_items)),
  };
}

export default function NotesPanel({
  grackleId,
  share,
  canEdit,
  onSeek,
}: {
  grackleId: string;
  share: string | null;
  /** Edit access (owner/org admin) — same bar the server enforces for generate/regenerate. */
  canEdit: boolean;
  onSeek: (seconds: number) => void;
}) {
  const [notes, setNotes] = useState<NotesOut | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [generating, setGenerating] = useState(false);
  const [canceling, setCanceling] = useState(false);
  const [generateError, setGenerateError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const copiedTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  const load = useCallback(async () => {
    try {
      const qs = share ? `?share=${encodeURIComponent(share)}` : "";
      const res = await api<NotesOut>("GET", `/grackles/${grackleId}/notes${qs}`);
      setNotes(res);
      setLoadError(null);
    } catch (err) {
      setLoadError(err instanceof ApiFail ? err.message : "Couldn't load notes.");
    }
  }, [grackleId, share]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    const shouldPoll = notes?.status === "queued" || notes?.status === "running";
    if (!shouldPoll) return;
    const id = setInterval(() => void load(), POLL_MS);
    return () => clearInterval(id);
  }, [notes?.status, load]);

  useEffect(() => {
    return () => {
      if (copiedTimeout.current) clearTimeout(copiedTimeout.current);
    };
  }, []);

  async function onGenerate(force: boolean) {
    setGenerating(true);
    setGenerateError(null);
    try {
      await api<NotesGenerateOut>("POST", `/grackles/${grackleId}/notes/generate`, { force });
      await load();
    } catch (err) {
      setGenerateError(err instanceof ApiFail ? err.message : "Couldn't start generating notes.");
    } finally {
      setGenerating(false);
    }
  }

  async function onCancel() {
    setCanceling(true);
    setGenerateError(null);
    try {
      await api<NotesCancelOut>("POST", `/grackles/${grackleId}/notes/cancel`);
      await load();
    } catch (err) {
      setGenerateError(err instanceof ApiFail ? err.message : "Couldn't cancel generating notes.");
    } finally {
      setCanceling(false);
    }
  }

  async function onCopyMarkdown() {
    if (!notes) return;
    try {
      await navigator.clipboard.writeText(notesToMarkdown(notes, grackleId));
      setCopied(true);
      if (copiedTimeout.current) clearTimeout(copiedTimeout.current);
      copiedTimeout.current = setTimeout(() => setCopied(false), 2500);
    } catch {
      // Clipboard may be denied; there is nothing else useful to do here.
    }
  }

  if (loadError && !notes) {
    return (
      <div className="watch-right-section notes-panel">
        <div className="gk-overline">Meeting notes</div>
        <Notice tone="error">{loadError}</Notice>
      </div>
    );
  }
  if (!notes) return null;

  const view = computeNotesView(notes, canEdit);
  if (!view.visible) return null;

  return (
    <div className="watch-right-section notes-panel">
      <div className="gk-overline">Meeting notes</div>

      {view.showGenerateButton ? (
        <div className="gk-sm">
          No notes yet.
          <div style={{ marginTop: 8 }}>
            <Button variant="secondary" onClick={() => void onGenerate(false)} loading={generating}>
              Generate notes
            </Button>
          </div>
        </div>
      ) : view.showUnavailable ? (
        <div className="gk-sm">
          {unavailableMessage(notes) ?? (
            <>
              Meeting notes aren't available for this recording.
              {notes.error_reason ? <div style={{ marginTop: 6 }}>{notes.error_reason}</div> : null}
            </>
          )}
          {canEdit ? (
            <div style={{ marginTop: 8 }}>
              <Button variant="secondary" onClick={() => void onGenerate(true)} loading={generating}>
                Generate notes
              </Button>
            </div>
          ) : null}
        </div>
      ) : view.showCanceled ? (
        <div className="gk-sm">
          Notes generation was canceled.
          <div style={{ marginTop: 8 }}>
            <Button variant="secondary" onClick={() => void onGenerate(true)} loading={generating}>
              Generate notes
            </Button>
          </div>
        </div>
      ) : view.showPending ? (
        <div className="gk-sm">
          {notes.status === "queued" ? "Queued — this page updates when notes are ready." : "Generating notes…"}
          {view.showCancelButton ? (
            <div style={{ marginTop: 8 }}>
              <Button variant="danger" icon="x" onClick={() => void onCancel()} loading={canceling}>
                Cancel
              </Button>
            </div>
          ) : null}
        </div>
      ) : null}

      {view.showCallout ? (
        <Notice>
          {view.calloutText}
          {view.showCalloutError ? (
            <div style={{ marginTop: 4 }}>{unavailableMessage(notes) ?? notes.error_reason}</div>
          ) : null}
          {canEdit ? (
            <div className="notes-actions" style={{ marginTop: 8 }}>
              <Button
                variant="secondary"
                icon="arrow-clockwise"
                onClick={() => void onGenerate(true)}
                loading={generating}
              >
                Retry
              </Button>
              {view.showCancelButton ? (
                <Button variant="danger" icon="x" onClick={() => void onCancel()} loading={canceling}>
                  Cancel
                </Button>
              ) : null}
            </div>
          ) : null}
        </Notice>
      ) : null}

      {generateError ? <Notice tone="error">{generateError}</Notice> : null}

      {view.showItems ? (
        <div className="notes-body">
          {(notes.status === "ready" || notes.status === "canceled") && provenanceLine(notes) ? (
            <div className="gk-caption">{provenanceLine(notes)}</div>
          ) : null}

          {notes.summary.length ? (
            <div className="notes-section">
              <div className="gk-overline">Summary</div>
              {notes.summary.map((item, i) => (
                <SummaryLine key={i} item={item} onSeek={onSeek} />
              ))}
            </div>
          ) : null}

          {notes.decisions.length ? (
            <div className="notes-section">
              <div className="gk-overline">Decisions</div>
              {notes.decisions.map((item, i) => (
                <DecisionLine key={i} item={item} onSeek={onSeek} />
              ))}
            </div>
          ) : null}

          {notes.action_items.length ? (
            <div className="notes-section">
              <div className="gk-overline">Action items</div>
              {notes.action_items.map((item, i) => (
                <ActionItemLine key={i} item={item} onSeek={onSeek} />
              ))}
            </div>
          ) : null}

          {notes.chapters.length ? (
            <div className="notes-section">
              <div className="gk-overline">Chapters</div>
              {notes.chapters.map((item, i) => (
                <SummaryLine key={i} item={item} onSeek={onSeek} />
              ))}
            </div>
          ) : null}

          <div className="notes-actions">
            <Button variant="ghost" onClick={() => void onCopyMarkdown()}>
              {copied ? "Copied" : "Copy as Markdown"}
            </Button>
            {view.showRegenerateButton ? (
              <Button variant="ghost" onClick={() => void onGenerate(true)} loading={generating}>
                Regenerate
              </Button>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
}
